import type { LitElement, PropertyValues } from 'lit'
import { property } from 'lit/decorators.js'
import type { Constructor } from './constructor'

export declare class IHostAriaLabel {
	/**
	 * The name the consumer gave this control. On a host that is not itself the
	 * control it is held here after the attribute is taken away, so
	 * `host.getAttribute('aria-label')` reads null on a correctly named host by
	 * design.
	 */
	ariaLabel: string | null
}

// The roles that make an element the control itself rather than a wrapper
// around one: WAI-ARIA 1.2's widget and composite widget roles. A host a
// container gave `listitem` or `none` is still a wrapper, and its control is
// still the one inside it.
const CONTROL_ROLES = new Set([
	'button',
	'checkbox',
	'combobox',
	'grid',
	'gridcell',
	'link',
	'listbox',
	'menu',
	'menubar',
	'menuitem',
	'menuitemcheckbox',
	'menuitemradio',
	'option',
	'progressbar',
	'radio',
	'radiogroup',
	'scrollbar',
	'searchbox',
	'slider',
	'spinbutton',
	'switch',
	'tab',
	'tablist',
	'tabpanel',
	'textbox',
	'tree',
	'treegrid',
	'treeitem',
])

/** Whether the host carries a control's role itself, which makes it the node its name belongs on. */
export function hostIsTheControl(host: Element): boolean {
	return CONTROL_ROLES.has(host.getAttribute('role') ?? '')
}

// Module-level rather than class fields: an exported class expression cannot
// carry a `#private` member through declaration emit.
const names = new WeakMap<Element, string | null>()
const stripping = new WeakSet<Element>()

/**
 * For a host whose focusable control lives in its own shadow root. That host
 * is a generic node, so an `aria-label` written on it names nothing any
 * assistive technology or role-and-name harness reads. This captures the name,
 * takes the attribute off the host so the control is announced once, and
 * re-renders; the component binds `this.ariaLabel` onto that control ahead of
 * any `label` or built-in default, because a name the consumer authored
 * outranks one the component would infer.
 *
 * A host carrying a control's role is itself the named node, so it keeps the
 * attribute — and a role arriving after the name puts the attribute back.
 *
 * Taking the attribute off re-enters the setter with null. That null is the
 * host's own doing, not the consumer clearing the name, so it is ignored for
 * as long as the removal runs and no longer: `ariaLabel = null` always clears.
 */
export const HostAriaLabelMixin = <T extends Constructor<LitElement>>(superClass: T) => {
	class HostAriaLabelElement extends superClass {
		override set ariaLabel(value: string | null) {
			if (value === null && stripping.has(this)) return
			const oldValue = names.get(this) ?? null
			names.set(this, value)
			if (value !== null && this.hasAttribute('aria-label') && !hostIsTheControl(this)) {
				stripping.add(this)
				try {
					this.removeAttribute('aria-label')
				} finally {
					stripping.delete(this)
				}
			}
			this.requestUpdate('ariaLabel', oldValue)
		}

		@property({ attribute: 'aria-label' })
		override get ariaLabel(): string | null {
			return names.get(this) ?? null
		}

		protected override willUpdate(changed: PropertyValues): void {
			super.willUpdate(changed)
			if (!hostIsTheControl(this)) return
			const name = names.get(this) || null
			if (name && this.getAttribute('aria-label') !== name) this.setAttribute('aria-label', name)
			if (!name && this.hasAttribute('aria-label')) this.removeAttribute('aria-label')
		}
	}
	return HostAriaLabelElement as Constructor<IHostAriaLabel> & T
}
